import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Label,
  Row,
  Table,
} from "reactstrap";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";

import AppLayout from "layout/AppLayout";
import axios from "axiosClient";
import ConfirmModal from "components/ConfirmModal";
import SelectInput from "components/common/SelectInput";
import { formatGmt5, GMT5_LABEL } from "utils/timezone";
import { money } from "./shared";

const STATUS_META = {
  pending: { color: "warning", label: "Waiting" },
  approved: { color: "success", label: "Approved" },
  rejected: { color: "danger", label: "Rejected" },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <Badge color={meta.color} className="text-white">
      {meta.label}
    </Badge>
  );
};

const discountOf = (request) =>
  request?.discountType === "percentage"
    ? `${request.discountValue}%`
    : money(request?.discountValue);

/**
 * Promocodes asked for from the till, waiting on a decision.
 *
 * The same requests carry Approve/Reject buttons in Telegram; whichever happens
 * first wins and the other is refused, so the two can never disagree.
 */
const PromocodeRequests = () => {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState(null);
  const [pendingDecision, setPendingDecision] = useState(null);

  const load = useCallback(
    async (nextStatus = status) => {
      setLoading(true);
      try {
        const { data } = await axios.post("/api/promocode-requests", {
          status: nextStatus,
          per_page: 100,
        });
        setItems(data?.items || []);
      } catch (error) {
        toast.error(
          error?.response?.data?.message || "Could not load the requests",
        );
      } finally {
        setLoading(false);
      }
    },
    [status],
  );

  useEffect(() => {
    load(status);
  }, [load, status]);

  const decide = async (request, decision) => {
    setDeciding(request._id);
    try {
      await axios.post(`/api/promocode-request/${request._id}/decision`, {
        status: decision,
      });
      toast.success(
        decision === "approved"
          ? `${request.code} approved`
          : `${request.code} rejected`,
      );
      await load(status);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not apply the decision",
      );
      // Somebody may have decided it in Telegram a moment ago.
      await load(status);
    } finally {
      setDeciding(null);
    }
  };

  return (
    <AppLayout>
      <div className="dashboard-wrapper">
        <Card className="h-100 m-4">
          <CardBody>
            <div className="d-flex justify-content-between align-items-start flex-wrap">
              <div>
                <CardTitle className="mb-1">Promocode requests</CardTitle>
                <p className="text-muted mb-0">
                  Discounts asked for at the till. A code exists as soon as it is
                  requested but discounts nothing until it is approved here or in
                  Telegram.
                </p>
              </div>
              <div className="mt-2">
                <Button
                  color="light"
                  className="mr-2"
                  tag={NavLink}
                  to="/app/promocodes"
                >
                  All promocodes
                </Button>
                <Button color="light" onClick={() => load(status)}>
                  Refresh
                </Button>
              </div>
            </div>

            <Row className="text-left mt-3">
              <Col md="3">
                <FormGroup>
                  <Label>Status</Label>
                  <SelectInput
                    value={status}
                    onChange={(value) => setStatus(value)}
                    options={[
                      { label: "Waiting", value: "pending" },
                      { label: "Approved", value: "approved" },
                      { label: "Rejected", value: "rejected" },
                    ]}
                  />
                </FormGroup>
              </Col>
            </Row>

            <div className="loading" hidden={!loading} />

            {!loading && items.length === 0 && (
              <p className="text-muted">Nothing here.</p>
            )}

            {!loading && items.length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <Table striped size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Discount</th>
                      <th>Candidate</th>
                      <th>Exam</th>
                      <th>Note</th>
                      <th>Requested by</th>
                      <th>{`Requested (${GMT5_LABEL})`}</th>
                      <th>Status</th>
                      <th style={{ width: 170 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((request) => (
                      <tr key={request._id}>
                        <td>
                          <strong>{request.code}</strong>
                        </td>
                        <td>{discountOf(request)}</td>
                        <td>
                          {request.candidate?.fullName || "—"}
                          {request.candidate?.pinpp ? (
                            <div className="text-muted text-small">
                              {request.candidate.pinpp}
                            </div>
                          ) : null}
                        </td>
                        <td className="text-muted">
                          {request.exam} {request.module}
                          {request.examDate ? (
                            <div className="text-small">{request.examDate}</div>
                          ) : null}
                        </td>
                        <td
                          className="text-muted text-small"
                          style={{ maxWidth: 260 }}
                        >
                          {request.note}
                        </td>
                        <td className="text-muted text-small">
                          {request.requestedBy?.username || "—"}
                        </td>
                        <td className="text-muted text-small">
                          {formatGmt5(request.createdAt)}
                        </td>
                        <td>
                          <StatusBadge status={request.status} />
                          {request.status !== "pending" &&
                          request.decidedBy?.username ? (
                            <div className="text-muted text-small">
                              by {request.decidedBy.username}
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {request.status === "pending" && (
                            <>
                              <Button
                                size="xs"
                                color="success"
                                className="mr-1"
                                disabled={deciding === request._id}
                                onClick={() =>
                                  setPendingDecision({
                                    request,
                                    decision: "approved",
                                  })
                                }
                              >
                                Approve
                              </Button>
                              <Button
                                size="xs"
                                color="danger"
                                disabled={deciding === request._id}
                                onClick={() =>
                                  setPendingDecision({
                                    request,
                                    decision: "rejected",
                                  })
                                }
                              >
                                Reject
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </CardBody>
        </Card>

        {pendingDecision && (
          <ConfirmModal
            modal={Boolean(pendingDecision)}
            setModal={() => setPendingDecision(null)}
            title={
              pendingDecision.decision === "approved"
                ? "Approve this promocode?"
                : "Reject this promocode?"
            }
            body={
              pendingDecision.decision === "approved"
                ? `${pendingDecision.request.code} will start working immediately for ${
                    pendingDecision.request.candidate?.fullName || "this candidate"
                  } — ${discountOf(pendingDecision.request)} off ${
                    pendingDecision.request.exam
                  } ${pendingDecision.request.module}.`
                : `${pendingDecision.request.code} will be archived and can never be used.`
            }
            action={() =>
              decide(pendingDecision.request, pendingDecision.decision)
            }
            confirmLabel={
              pendingDecision.decision === "approved"
                ? "Approve promocode"
                : "Reject promocode"
            }
            confirmColor={
              pendingDecision.decision === "approved" ? "primary" : "danger"
            }
          />
        )}
      </div>
    </AppLayout>
  );
};

export default PromocodeRequests;
